import React, { useRef, useState } from 'react';
import InsightsFixedTip from './insightsFixedTip';
import { MomentumPoint } from './insightsMatchMomentum';

interface Props {
    points: MomentumPoint[];
}

const WIDTH = 720;
const HEIGHT = 140;
const PAD_X = 16;
const PAD_TOP = 12;
const PAD_BOTTOM = 20;

const signed = (n: number): string => `${n > 0 ? '+' : ''}${n}`;

/** Simple cumulative momentum line for our club (heuristic). */
const InsightsMomentumChart: React.FC<Props> = ({ points }) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const chartRef = useRef<SVGSVGElement>(null);

    if (points.length < 2) {
        return <div className="insightsEmpty">Not enough events for momentum.</div>;
    }

    const values = points.map((p) => p.value);
    const minV = Math.min(0, ...values);
    const maxV = Math.max(0, ...values);
    const range = Math.max(1, maxV - minV);
    const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
    const stepX = (WIDTH - PAD_X * 2) / (points.length - 1);
    const xAt = (i: number): number => PAD_X + i * stepX;
    const yAt = (v: number): number => PAD_TOP + ((maxV - v) / range) * plotH;
    const yZero = yAt(0);
    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.value)}`).join(' ');

    const hover = hoverIndex != null ? points[hoverIndex] : null;
    const tipX = hoverIndex != null ? xAt(hoverIndex) / WIDTH : 0;
    const tipY = hover != null ? yAt(hover.value) / HEIGHT : 0;

    return (
        <div className="insightsMomentumChart">
            {hover && hoverIndex != null && hover.kind !== 'start' ? (
                <InsightsFixedTip
                    x={tipX}
                    y={tipY}
                    container={chartRef.current}
                    className="insightsChartTooltip"
                >
                    <b>{hover.headline}</b>
                    <i>{hover.label}</i>
                    {hover.playerLine ? <span>{hover.playerLine}</span> : null}
                    {hover.typeLine ? <span>{hover.typeLine}</span> : null}
                    <span>
                        Score {hover.usScore}:{hover.themScore}
                    </span>
                    <span>
                        Momentum {signed(hover.delta)} → {signed(hover.value)}
                    </span>
                </InsightsFixedTip>
            ) : null}
            <svg
                ref={chartRef}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                className="insightsTrendSvg"
                role="img"
                aria-label="Match momentum"
            >
                <line
                    x1={PAD_X}
                    y1={yZero}
                    x2={WIDTH - PAD_X}
                    y2={yZero}
                    className="insightsChartBaseline"
                />
                <path d={path} className="insightsTrendLine" />
                {points.map((p, i) =>
                    i === 0 ? null : (
                        <g key={p.eventId || i}>
                            <circle
                                cx={xAt(i)}
                                cy={yAt(p.value)}
                                r={hoverIndex === i ? 4.5 : 2.5}
                                className={`insightsTrendDot${hoverIndex === i ? ' isActive' : ''}`}
                            />
                            <circle
                                cx={xAt(i)}
                                cy={yAt(p.value)}
                                r={9}
                                className="insightsTrendHit"
                                onMouseEnter={() => setHoverIndex(i)}
                                onMouseLeave={() => setHoverIndex(null)}
                            />
                        </g>
                    )
                )}
            </svg>
            <p className="insightsTableNote">
                Heuristic control: +3/−3 goals, +1 save / exclusion drawn, −1 turnover / we
                excluded.
            </p>
        </div>
    );
};

export default InsightsMomentumChart;
